import React, { useEffect, useState, useRef } from 'react';
import { Loader2, MessageCircle, Phone, Mail, MapPin, Truck, Calendar as CalIcon, X as XIcon, Plus, Image as ImageIcon } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../components/ui/popover';
import { Calendar as ShadCalendar } from '../components/ui/calendar';
import { format } from 'date-fns';
import { toast } from 'sonner';
import { useAuth, formatApiError } from '../auth/AuthContext';
import LoginForm from '../auth/LoginForm';
import { JobDetailsEditor } from '../components/JobDetailsEditor';
import { StatusButtons } from '../components/driver/StatusButtons';
import { NavButtons, CustomerInfo } from '../components/driver/JobInfo';
import { JobPhotos } from '../components/JobPhotos';
import { JobInventoryEditor } from '../components/admin/JobInventoryEditor';
import { ProofPanel } from '../components/driver/ProofPanel';
import { BalancePanel } from '../components/driver/BalancePanel';
import { MyWeek } from '../components/driver/MyWeek';
import { ReportActions } from '../components/driver/ReportActions';
import { CancelBooking } from '../components/CancelBooking';
import { RunningLate, LateBadge, useLateness } from '../components/RunningLate';
import { DriverReports } from '../components/driver/DriverReports';
import { JobCrew, MyPorters, DriverNotifications } from '../components/driver/DriverCrew';
import { MyWages, MySchedule } from '../components/driver/MyWagesSchedule';
import { DriverCalendar } from '../components/driver/DriverCalendar';
import { PorterDirectory } from '../components/driver/DriverPorters';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const resolveAvatar = (p) => (!p ? '' : /^https?:/.test(p) ? p : `${BACKEND}${p}`);

const todayIso = () => new Date().toISOString().slice(0, 10);
const toIsoDate = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
};

const customerWhatsApp = (item) => {
  const txt = encodeURIComponent(`Hi ${item.name || 'there'}, this is your Move Van Ltd driver for booking #${item.ref}. On my way!`);
  return `https://wa.me/${(item.phone || '').replace(/\D/g, '').replace(/^0/, '44')}?text=${txt}`;
};

/* -------- My time off -------- */
const TimeOffPanel = ({ unavailable, onChange, authFetch }) => {
  const [picking, setPicking] = useState(false);
  const sorted = (unavailable || []).slice().sort();

  const toggle = async (dateStr, forceOff) => {
    try {
      const { data } = await authFetch('post', '/driver/unavailable', { date: dateStr, off: forceOff });
      onChange(data.unavailable || []);
    } catch (e) {
      toast.error(formatApiError(e?.response?.data?.detail));
    }
  };

  return (
    <section className="mb-6 rounded-xl border border-slate-200 bg-white p-5" data-testid="driver-timeoff-panel">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">My time off</div>
          <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)]">I'm off these days</h2>
          <p className="text-xs text-slate-500 mt-1">The office won't auto-assign you to any job on these days.</p>
        </div>
        <Popover open={picking} onOpenChange={setPicking}>
          <PopoverTrigger asChild>
            <button className="inline-flex items-center gap-1.5 bg-[color:var(--mv-navy)] hover:bg-[#13315c] text-white text-sm font-bold px-4 h-11 rounded-md" data-testid="driver-add-off-btn">
              <Plus className="w-3.5 h-3.5" /> Add a day off
            </button>
          </PopoverTrigger>
          <PopoverContent className="w-auto p-0" align="end">
            <ShadCalendar
              mode="single"
              onSelect={async (d) => {
                if (!d) return;
                setPicking(false);
                await toggle(toIsoDate(d), true);
                toast.success(`Marked ${format(d, 'EEE, d MMM')} as off.`);
              }}
              disabled={{ before: new Date(new Date().setHours(0, 0, 0, 0)) }}
              initialFocus
            />
          </PopoverContent>
        </Popover>
      </div>
      {sorted.length === 0 ? (
        <div className="mt-3 text-xs text-slate-500">You've got no days off booked.</div>
      ) : (
        <div className="mt-3 flex flex-wrap gap-2" data-testid="driver-off-list">
          {sorted.map(d => (
            <span key={d} className="inline-flex items-center gap-1.5 bg-amber-50 border border-amber-200 text-amber-900 text-xs font-bold px-3 py-1 rounded-full">
              <CalIcon className="w-3 h-3" /> {format(new Date(d), 'EEE, d MMM')}
              <button type="button" onClick={() => toggle(d, false)} className="hover:text-red-700 -mr-2 w-8 h-8 inline-flex items-center justify-center" aria-label="Remove day off" data-testid={`driver-off-remove-${d}`}><XIcon className="w-4 h-4" /></button>
            </span>
          ))}
        </div>
      )}
    </section>
  );
};

const DriverSelfAvatar = ({ driver, authFetch, onUploaded }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);
  const size = 64;
  const onPick = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 15 * 1024 * 1024) { toast.error('Image must be under 15MB.'); return; }
    setUploading(true);
    try {
      const b64 = await new Promise((res, rej) => {
        const r = new FileReader();
        r.onload = () => res(r.result);
        r.onerror = rej;
        r.readAsDataURL(f);
      });
      await authFetch('post', '/driver/avatar', { photo: b64 });
      toast.success('Profile photo updated.');
      onUploaded?.();
    } catch (err) {
      toast.error(formatApiError(err?.response?.data?.detail));
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = '';
    }
  };
  return (
    <button
      type="button"
      onClick={onPick}
      disabled={uploading}
      title={driver?.avatar_url ? 'Tap to change your photo' : 'Tap to add a photo'}
      className="relative group shrink-0"
      data-testid="driver-self-avatar-btn"
    >
      {driver?.avatar_url ? (
        <img
          src={resolveAvatar(driver.avatar_url)}
          alt={driver.name}
          className="rounded-full object-cover border-2 shadow-sm"
          style={{ width: size, height: size, borderColor: driver.color }}
        />
      ) : (
        <span
          className="rounded-full text-white flex items-center justify-center font-black border-2 border-white shadow-sm"
          style={{ background: driver?.color || '#64748b', width: size, height: size, fontSize: 22 }}
        >
          {(driver?.name || '?').slice(0, 1).toUpperCase()}
        </span>
      )}
      <span className="absolute -bottom-1 -right-1 bg-[color:var(--mv-orange)] text-white rounded-full p-1 shadow">
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ImageIcon className="w-3 h-3" />}
      </span>
      <input ref={fileRef} type="file" accept="image/*,.heic,.heif" className="hidden" onChange={onFile} data-testid="driver-self-avatar-file" />
    </button>
  );
};

function Schedule() {
  const { user, authFetch, logout } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [unavailable, setUnavailable] = useState([]);
  const [refreshKey, setRefreshKey] = useState(0);

  const [crew, setCrew] = useState(null);
  const [notes, setNotes] = useState(null);
  const loadNotes = () => authFetch('get', '/driver/notifications').then(({ data }) => setNotes(data)).catch(() => {});

  const load = async () => {
    try {
      const { data } = await authFetch('get', '/driver/schedule');
      authFetch('get', '/driver/porters').then(({ data }) => setCrew(data.jobs)).catch(() => {});
      loadNotes();
      setData(data);
      setRefreshKey(k => k + 1);
      setUnavailable(data.unavailable || []);
    } catch (e) {
      if (e?.response?.status === 401) logout();
      else toast.error(formatApiError(e?.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);
  const late = useLateness(authFetch, todayIso(), (data?.bookings || []).map(x => `${x.id}${x.running_late || 0}${x.booking?.time}`).join(','));

  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /></div>;
  if (!data) return null;

  const groups = {};
  (data.bookings || []).forEach(b => {
    const d = b.booking?.date || 'unassigned';
    (groups[d] = groups[d] || []).push(b);
  });
  const dates = Object.keys(groups).sort();
  const today = todayIso();

  return (
    <div className="min-h-[60vh] bg-slate-50 pt-8 pb-24 px-4">
      <div className="max-w-2xl mx-auto">
        <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <DriverSelfAvatar driver={data.driver} authFetch={authFetch} onUploaded={load} />
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-[0.22em] font-black" style={{ color: data.driver?.color }}>Driver portal</div>
              <h1 className="font-display text-3xl font-extrabold text-[color:var(--mv-navy)] mt-1 truncate">Hi {data.driver?.name?.split(' ')[0] || ''}</h1>
              <div className="text-xs text-slate-500 mt-1">Signed in as <strong>{user?.email}</strong></div>
            </div>
          </div>
          <button onClick={logout} className="text-sm font-semibold text-slate-600 hover:text-[color:var(--mv-orange)] h-11 px-4 rounded-md border border-slate-200 bg-white" data-testid="driver-logout-btn">Sign out</button>
        </div>

        <div className="rounded-xl border border-slate-200 bg-white p-4 mb-6">
          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Your schedule</div>
          <div className="font-display text-2xl font-black text-[color:var(--mv-navy)] mt-1">{(data.bookings || []).length} upcoming job{(data.bookings || []).length === 1 ? '' : 's'}</div>
          <div className="text-xs text-slate-500 mt-1">Next 21 days</div>
        </div>

        <DriverCalendar authFetch={authFetch} refreshKey={refreshKey} />

        <DriverNotifications data={notes} authFetch={authFetch} onRead={loadNotes} />

        <MyPorters bookings={data.bookings || []} crewByJob={crew} />

        <PorterDirectory authFetch={authFetch} refreshKey={refreshKey} />

        <DriverReports authFetch={authFetch} jobs={data.bookings || []} onChanged={() => setRefreshKey(k => k + 1)} />

        <TimeOffPanel unavailable={unavailable} onChange={setUnavailable} authFetch={authFetch} />

        <MyWeek authFetch={authFetch} refreshKey={refreshKey} />

        <MySchedule authFetch={authFetch} refreshKey={refreshKey} />

        <MyWages authFetch={authFetch} refreshKey={refreshKey} />

        {dates.length === 0 && (
          <div className="rounded-xl border border-dashed border-slate-200 bg-white p-8 text-center text-sm text-slate-500" data-testid="driver-empty-state">
            No jobs assigned to you yet — your schedule will show up here as the office assigns bookings.
          </div>
        )}

        {dates.map(d => (
          <section key={d} className="mb-6" data-testid="driver-date-group">
            <h2 className="font-display text-lg font-extrabold text-[color:var(--mv-navy)] mb-2 flex items-center gap-2">
              <CalIcon className="w-4 h-4 text-[color:var(--mv-orange)]" />
              {new Date(d).toLocaleDateString('en-GB', { weekday: 'short', day: '2-digit', month: 'short', year: 'numeric' })}
              {d === today && <span className="text-[10px] bg-emerald-100 text-emerald-900 font-black uppercase tracking-wider px-2 py-0.5 rounded-full">Today</span>}
            </h2>
            <div className="space-y-3">
              {groups[d].map(b => {
                const sameDay = b.booking?.same_day;
                return (
                  <div key={b.id} id={`job-${b.id}`} className={`scroll-mt-4 rounded-xl border bg-white p-4 shadow-sm ${sameDay ? 'border-red-300' : 'border-slate-200'}`} data-testid="driver-job-card">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div className="min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="font-black text-[color:var(--mv-navy)] text-lg">{b.booking?.time || '—'}</span>
                          <span className="text-slate-600 text-xs">·</span>
                          <span className="font-bold text-[color:var(--mv-navy)]">{b.name}</span>
                          <span className="text-xs font-bold text-slate-600">#{b.ref}</span>
                          {sameDay && <span className="text-[10px] font-black tracking-wider uppercase px-2 py-0.5 rounded-full bg-red-100 text-red-800">Same-day</span>}
                          {d === today && <LateBadge p={late[b.id]} ref_={b.ref} />}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                          <MapPin className="w-3 h-3" /> {b.from_postcode || '—'} → {b.to_postcode || '—'}
                        </div>
                        <div className="text-xs text-slate-600 mt-1 flex items-center gap-1">
                          <Truck className="w-3 h-3" /> {b.van_recommendation}
                          {b.hours_estimate && <> · {b.hours_estimate}</>}
                          {b.property_size && <> · {b.property_size}</>}
                        </div>
                        <div className="text-xs text-slate-600 mt-2 flex items-center flex-wrap gap-x-3 gap-y-1">
                          {b.phone && <a href={`tel:${b.phone}`} className="flex items-center gap-1 text-[color:var(--mv-navy)] font-bold"><Phone className="w-3 h-3" />{b.phone}</a>}
                          {b.email && <a href={`mailto:${b.email}`} className="flex items-center gap-1 hover:text-[color:var(--mv-orange)]"><Mail className="w-3 h-3" />{b.email}</a>}
                        </div>
                      </div>
                      {b.booking?.final_price !== undefined && (
                        <div className="sm:text-right">
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500">Job total</div>
                          <div className="font-display text-xl font-black text-[color:var(--mv-navy)] leading-none" data-testid={`driver-job-price-${b.ref}`}>£{b.booking.final_price}</div>
                        </div>
                      )}
                    </div>
                    <div className="mt-3 space-y-3">
                      <CustomerInfo job={b} />
                      <JobInventoryEditor key={`inv-${b.id}`} job={b} authFetch={authFetch} onSaved={load} showWastePhotos={false} />
                      <JobPhotos job={b} authFetch={authFetch} />
                      <JobPhotos job={b} authFetch={authFetch} kind="waste" title="Waste photos" />
                      <NavButtons job={b} />
                      <StatusButtons job={b} authFetch={authFetch} onUpdated={load} />
                      <JobCrew job={b} crewInfo={crew?.[b.id]} authFetch={authFetch} onUpdated={load} />
                      <ProofPanel job={b} authFetch={authFetch} onUpdated={load} />
                      <BalancePanel job={b} authFetch={authFetch} onUpdated={load} />
                      <ReportActions job={b} authFetch={authFetch} onUpdated={load} />
                      {d === today && !b.completed_at && <RunningLate job={b} authFetch={authFetch} projection={late[b.id]} onSaved={load} />}
                      {!b.completed_at && <CancelBooking job={b} authFetch={authFetch} onDone={load} />}
                      <JobDetailsEditor item={b} authFetch={authFetch} onSaved={load} />
                    </div>
                    {b.phone && (
                      <div className="mt-3 pt-3 border-t border-slate-100">
                        <a href={customerWhatsApp(b)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 bg-[#25D366] hover:bg-[#1ebc5a] text-white text-sm font-bold px-4 h-11 rounded-md" data-testid="driver-wa-btn">
                          <MessageCircle className="w-3.5 h-3.5" /> WhatsApp customer
                        </a>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}

export default function Driver() {
  const { user, loading, logout } = useAuth();
  if (loading) return <div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="w-5 h-5 animate-spin text-[color:var(--mv-orange)]" /></div>;
  if (!user) return <LoginForm title="Driver portal" subtitle="Sign in with the email and password your office gave you." expectedRole="driver" />;
  if (user.role !== 'driver') {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-6 text-center">
        <div>
          <p className="text-slate-600">You're signed in as the owner. Head to the admin dashboard.</p>
          <button onClick={logout} className="mt-3 text-sm font-bold text-[color:var(--mv-orange)]">Sign out</button>
        </div>
      </div>
    );
  }
  return <Schedule />;
}
